<template>
    <div class="modal-backdrop" @click.self="$emit('close')" @keydown.esc="$emit('close')" tabindex="-1">
        <div class="modal" role="dialog" aria-modal="true" aria-labelledby="preview-title">
            <header>
                <h2 id="preview-title">Resumen de tu cotización</h2>
                <button class="close" @click="$emit('close')" aria-label="Cerrar">×</button>
            </header>

            <div v-if="loading" class="loading">Calculando precio…</div>

            <div v-else class="content">
                <section class="customer">
                    <h3>Datos de contacto</h3>
                    <p><strong>Nombre:</strong> {{ payload.contactName }}</p>
                    <p><strong>Email:</strong> {{ payload.contactEmail }}</p>
                    <p v-if="payload.contactPhone"><strong>Teléfono:</strong> {{ payload.contactPhone }}</p>
                </section>

                <section class="specs">
                    <h3>Especificaciones</h3>
                    <p>
                        <strong>Dimensiones:</strong>
                        {{ payload.dimensionsMm.x }} × {{ payload.dimensionsMm.y }} × {{ payload.dimensionsMm.z }} mm
                    </p>
                    <p v-if="volumeMm3"><strong>Volumen:</strong> {{ formatNumber(volumeMm3 / 1000, 2) }} cm³</p>
                    <p><strong>Cantidad:</strong> {{ payload.quantity }}</p>
                </section>

                <section v-if="payload.observations && payload.observations.trim()" class="observations">
                    <h3>Observaciones</h3>
                    <p class="observations-text">{{ payload.observations }}</p>
                </section>

                <section v-if="preview" class="pricing">
                    <h3>Cálculo automático ({{ preview.material.name }})</h3>
                    <p>Peso estimado: <strong>{{ formatNumber(preview.pricing.weightG, 2) }} g</strong></p>
                    <p v-if="preview.pricing.estTimeMin != null">
                        Tiempo de impresión estimado: <strong>{{ formatTime(preview.pricing.estTimeMin) }}</strong>
                    </p>
                    <p class="total">
                        Precio por unidad: <strong>{{ formatCurrency(preview.pricing.finalPrice) }}</strong>
                    </p>
                    <p class="total grand">
                        TOTAL ({{ preview.quantity }} u.): <strong>{{ formatCurrency(preview.totalForQuantity) }}</strong>
                    </p>
                    <small class="disclaimer">
                        Este es un cálculo estimado que considera material, energía, desgaste de la
                        máquina y margen de operación. El precio final puede ajustarse tras la revisión del modelo.
                    </small>
                </section>

                <section v-else-if="previewError" class="pricing pricing-warn">
                    <h3>Cálculo no disponible</h3>
                    <p>{{ previewError }}</p>
                    <small>La solicitud se enviará para revisión manual y recibirás la cotización por correo.</small>
                </section>

                <p v-if="submitError" class="err-msg" role="alert">{{ submitError }}</p>

                <footer>
                    <button type="button" class="secondary" @click="$emit('close')" :disabled="submitting">
                        Cancelar
                    </button>
                    <button type="button" class="primary" @click="onConfirm" :disabled="loading || submitting">
                        {{ submitting ? 'Enviando…' : 'Confirmar y enviar solicitud' }}
                    </button>
                </footer>
            </div>
        </div>
    </div>
</template>

<script>
import { getQuotePreview } from '@/services/print3dService';
import { formatNumberRAE, formatCurrencyRAE } from '@/utils/numberFormat';

export default {
    name: 'QuotePreviewModal',
    props: {
        payload: { type: Object, required: true },
        file: { type: File, default: null },
        volumeMm3: { type: Number, default: null },
    },
    emits: ['confirm', 'close'],
    data() {
        return {
            loading: true,
            preview: null,
            previewError: '',
            submitting: false,
            submitError: '',
        };
    },
    async mounted() {
        document.addEventListener('keydown', this.onEsc);
        if (!this.volumeMm3 || this.volumeMm3 <= 0) {
            this.previewError = 'Volumen del modelo no disponible — no se pudo estimar el precio.';
            this.loading = false;
            return;
        }
        const { preview, error } = await getQuotePreview({
            materialId: this.payload.materialId,
            volumeMm3: this.volumeMm3,
            quantity: this.payload.quantity,
            categoryId: this.payload.categoryId,
        });
        if (error) {
            this.previewError = error;
        } else {
            this.preview = preview;
        }
        this.loading = false;
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.onEsc);
    },
    methods: {
        onEsc(e) {
            if (e.key === 'Escape' && !this.submitting) this.$emit('close');
        },
        async onConfirm() {
            this.submitting = true;
            this.submitError = '';
            try {
                await this.$emit('confirm');
            } catch (e) {
                this.submitError = e?.message || 'No se pudo enviar la solicitud.';
                this.submitting = false;
            }
        },
        setSubmitError(message) {
            this.submitError = message;
            this.submitting = false;
        },
        formatNumber(value, digits = 2) {
            return formatNumberRAE(value, digits);
        },
        formatCurrency(value) {
            return formatCurrencyRAE(value);
        },
        formatTime(minutes) {
            const m = Number(minutes);
            if (!isFinite(m) || m <= 0) return '—';
            const h = Math.floor(m / 60);
            const rem = Math.round(m % 60);
            if (h <= 0) return `${rem} min`;
            return `${h} h ${rem} min`;
        },
    },
};
</script>

<style scoped>
.modal-backdrop {
    position: fixed; inset: 0; background: rgba(15,23,42,0.55);
    display: flex; align-items: center; justify-content: center; z-index: 1000;
}
.modal {
    background: white; border-radius: 12px; max-width: 560px; width: 92%;
    max-height: 90vh; overflow-y: auto; padding: 1.5rem;
    box-shadow: 0 20px 50px rgba(0,0,0,0.3);
}
header {
    display: flex; justify-content: space-between; align-items: center;
    border-bottom: 1px solid #e2e8f0; padding-bottom: 0.5rem; margin-bottom: 1rem;
}
header h2 { margin: 0; color: #2c3e50; font-size: 1.1rem; }
.close { background: transparent; border: none; font-size: 1.5rem; cursor: pointer; color: #64748b; }
.content section { margin-bottom: 1rem; padding-bottom: 0.75rem; border-bottom: 1px dashed #e2e8f0; }
.content section:last-of-type { border-bottom: none; }
.content h3 { margin: 0 0 0.4rem 0; color: #3d566b; font-size: 0.95rem; }
.content p { margin: 0.2rem 0; font-size: 0.9rem; }
.pricing { background: rgba(82,109,130,0.07); padding: 0.75rem; border-radius: 8px; }
.pricing-warn { background: rgba(251,191,36,0.12); }
.total { margin-top: 0.5rem; padding-top: 0.4rem; border-top: 1px solid #cbd5e1; }
.total.grand { font-size: 1rem; color: #111827; }
.disclaimer { display: block; margin-top: 0.5rem; color: #64748b; font-style: italic; }
.observations-text {
    white-space: pre-wrap;
    background: #f8fafc;
    padding: 0.5rem 0.7rem;
    border-radius: 6px;
    margin: 0;
    font-size: 0.9rem;
    color: #2c3e50;
}
.err-msg {
    background: rgba(185,28,28,0.08); color: #991b1b;
    padding: 0.5rem 0.7rem; border-radius: 6px; font-size: 0.88rem;
}
footer { display: flex; gap: 0.5rem; justify-content: flex-end; padding-top: 0.5rem; }
.primary {
    padding: 0.55rem 1rem; background: #526d82; color: white; border: none;
    border-radius: 6px; cursor: pointer; font-family: inherit;
}
.primary:hover:not(:disabled) { background: #3d566b; }
.primary:disabled { opacity: 0.55; cursor: not-allowed; }
.secondary {
    padding: 0.55rem 1rem; background: white; color: #2c3e50;
    border: 1px solid #cbd5e1; border-radius: 6px; cursor: pointer; font-family: inherit;
}
.secondary:disabled { opacity: 0.55; cursor: not-allowed; }
.loading { padding: 2rem; text-align: center; color: #64748b; }
</style>
